/* eslint-disable */
import React, { useState, useEffect, useCallback, memo } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, useMediaQuery, ButtonGroup, Button, IconButton, Tooltip, alpha, Fab, Menu, MenuItem } from '@mui/material';

// Icons
import MenuIcon from '@mui/icons-material/Menu';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import FlightIcon from '@mui/icons-material/Flight';

// Components
import NotificationBell from './Notification';
import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import LogoSection from '../LogoSection';
import MobileSection from './MobileSection';
import ProfileSection from './ProfileSection';
import { useDispatch, useSelector } from 'store';
import { openDrawer } from 'store/slices/menu';
import TravelPlanForm from 'views/trip/travelPlan';
import TravelRequestForm from 'views/trip/travel-request-form';
import TripPlanSearchDialog from 'views/trip/TripPlanSearchDialog';
import { themeColors } from 'views/ConstantColors';

const TimeBox = ({ children }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: alpha(themeColors.primary.lighter, 0.8),
            borderRadius: '12px',
            marginRight: 2,
            backdropFilter: 'blur(8px)',
            boxShadow: `0 4px 12px ${alpha(themeColors.primary.main, 0.1)}`,
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 16px ${alpha(themeColors.primary.main, 0.2)}`
            },
            '@media (max-width: 600px)': {
                padding: '6px 12px',
                '& .date-display': {
                    display: 'none'
                }
            }
        }}
    >
        {children}
    </Box>
);

const MobileFab = memo(({ handleFabClick, isProcessingClick, themeColors, fabMenuAnchor, handleFabMenuClose, handleMenuItemClick }) => (
    <Box
        sx={{
            position: 'fixed',
            right: 20,
            bottom: 20,
            zIndex: 1050,
            transform: 'translateZ(0)'
        }}
    >
        <Fab
            color="primary"
            onClick={handleFabClick}
            disabled={isProcessingClick}
            sx={{
                background: `linear-gradient(45deg, ${themeColors.primary.main}, ${themeColors.primary.dark})`,
                boxShadow: `0 8px 16px ${alpha(themeColors.primary.main, 0.3)}`,
                '&:hover': {
                    background: `linear-gradient(45deg, ${themeColors.primary.dark}, ${themeColors.primary.main})`
                }
            }}
        >
            <AddIcon />
        </Fab>
        <Menu
            anchorEl={fabMenuAnchor}
            open={Boolean(fabMenuAnchor)}
            onClose={handleFabMenuClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            PaperProps={{
                sx: {
                    mb: 1.5,
                    minWidth: 200,
                    borderRadius: 3,
                    boxShadow: `0 8px 24px ${alpha(themeColors.primary.main, 0.2)}`,
                    '& .MuiMenuItem-root': {
                        px: 2,
                        py: 1.5,
                        gap: 2,
                        borderRadius: 2,
                        margin: '4px 8px',
                        '&:hover': {
                            backgroundColor: alpha(themeColors.primary.main, 0.1)
                        }
                    }
                }
            }}
        >
            <MenuItem onClick={() => handleMenuItemClick('request')}>
                <FlightIcon sx={{ color: themeColors.primary.main }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Request & Plan</Typography>
            </MenuItem>
            <MenuItem onClick={() => handleMenuItemClick('plan')}>
                <EditCalendarIcon sx={{ color: themeColors.primary.main }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Travel Plan</Typography>
            </MenuItem>
        </Menu>
    </Box>
));

const MobileHeader = memo(({ drawerOpen, dispatch, openDrawer, themeColors, formatTime, currentTime }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            p: { xs: 1, sm: 1.5 },
            bgcolor: alpha(themeColors.background.default, 0.95),
            backdropFilter: 'blur(10px)',
            borderBottom: `1px solid ${alpha(themeColors.primary.main, 0.1)}`,
            boxShadow: `0 4px 12px ${alpha(themeColors.primary.main, 0.1)}`,
            position: 'sticky',
            top: 0,
            zIndex: 1000
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <IconButton
                onClick={() => dispatch(openDrawer(!drawerOpen))}
                sx={{
                    bgcolor: alpha(themeColors.primary.main, 0.1),
                    borderRadius: 2,
                    p: { xs: 0.75, sm: 1 },
                    '&:hover': {
                        bgcolor: alpha(themeColors.primary.main, 0.2)
                    }
                }}
            >
                <MenuIcon sx={{ color: themeColors.primary.main, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            </IconButton>
            <TimeBox>
                <AccessTimeIcon sx={{ color: themeColors.primary.main, fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                <Typography variant="subtitle2" sx={{ color: themeColors.primary.main, fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {formatTime(currentTime)}
                </Typography>
            </TimeBox>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            <ProfileSection />
        </Box>
    </Box>
));

const Header = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const matchDownSm = useMediaQuery(theme.breakpoints.down('sm'));
    const { layout } = useConfig();

    const [activeButton, setActiveButton] = useState('request');
    const [currentTime, setCurrentTime] = useState(new Date());
    const [openPlanRequestDialog, setOpenPlanRequestDialog] = useState(false);
    const [openTravelPlanForm, setOpenTravelPlanForm] = useState(false);
    const [openTravelRequestForm, setOpenTravelRequestForm] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [fabMenuAnchor, setFabMenuAnchor] = useState(null);
    const [isProcessingClick, setIsProcessingClick] = useState(false);

    // Improved FAB click handler with debounce
    const handleFabClick = useCallback((event) => {
        if (isProcessingClick) return;

        setIsProcessingClick(true);
        setFabMenuAnchor(event.currentTarget);

        // Reset processing state after a short delay
        setTimeout(() => {
            setIsProcessingClick(false);
        }, 300);
    }, [isProcessingClick]);

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const handleButtonClick = useCallback((buttonType) => {
        setActiveButton(buttonType);
    }, []);

    const handleClosePlanRequestDialog = useCallback(() => setOpenPlanRequestDialog(false), []);

    const handleOpenPlanRequestDialog = useCallback(() => {
        setOpenPlanRequestDialog(true);
        handleButtonClick('request');
    }, [handleButtonClick]);

    const handleDirectTravelPlan = useCallback(() => {
        setOpenTravelPlanForm(true);
        handleButtonClick('plan');
    }, [handleButtonClick]);

    const handleOpenTravelPlanForm = useCallback(() => {
        setOpenTravelPlanForm(true);
        setOpenPlanRequestDialog(false);
    }, []);

    const handleCloseTravelPlanForm = useCallback(() => setOpenTravelPlanForm(false), []);

    const handleOpenTravelRequestForm = useCallback((plan) => {
        setSelectedPlan(plan);
        setOpenTravelRequestForm(true);
        setOpenPlanRequestDialog(false);
    }, []);

    const handleCloseTravelRequestForm = useCallback(() => {
        setOpenTravelRequestForm(false);
        setSelectedPlan(null);
    }, []);

    const handleTravelPlanSuccess = useCallback((planData) => {
        handleCloseTravelPlanForm();
        handleOpenTravelRequestForm(planData);
    }, [handleCloseTravelPlanForm, handleOpenTravelRequestForm]);

    const handleFabMenuClose = useCallback(() => {
        setFabMenuAnchor(null);
    }, []);

    const formatTime = useCallback((date) =>
        date.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        }), []);

    const formatDate = useCallback((date) =>
        date.toLocaleDateString('en-GB', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        }), []);

    // Improved menu item click handlers
    const handleMenuItemClick = useCallback((action) => {
        // Close menu first
        handleFabMenuClose();

        // Use setTimeout to ensure menu is closed before opening new dialog
        setTimeout(() => {
            if (action === 'request') {
                handleOpenPlanRequestDialog();
            } else if (action === 'plan') {
                handleDirectTravelPlan();
            }
        }, 100);
    }, [handleFabMenuClose, handleOpenPlanRequestDialog, handleDirectTravelPlan]);

    return (
        <>
            {matchDownMd ? (
                <>
                    <MobileHeader
                        drawerOpen={drawerOpen}
                        dispatch={dispatch}
                        openDrawer={openDrawer}
                        themeColors={themeColors}
                        formatTime={formatTime}
                        currentTime={currentTime}
                    />
                    <MobileFab
                        handleFabClick={handleFabClick}
                        isProcessingClick={isProcessingClick}
                        themeColors={themeColors}
                        fabMenuAnchor={fabMenuAnchor}
                        handleFabMenuClose={handleFabMenuClose}
                        handleMenuItemClick={handleMenuItemClick}
                    />
                </>
            ) : (
                <Box
                    component="header"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1, md: 2 },
                        width: '100%',
                        p: 1,
                        backgroundColor: themeColors.background.default,
                        borderBottom: `1px solid ${themeColors.primary.lighter}`,
                        minHeight: 64,
                        boxShadow: `0 1px 3px ${themeColors.grey[200]}`,
                        transition: 'all 0.3s ease-in-out'
                    }}
                >
                    <Box
                        sx={{
                            width: { xs: 'auto', md: 228 },
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: { xs: 'none', md: 'block' },
                                flexGrow: 1
                            }}
                        >
                            <LogoSection />
                        </Box>
                        {(layout === LAYOUT_CONST.VERTICAL_LAYOUT || (layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && matchDownMd)) && (
                            <IconButton
                                onClick={() => dispatch(openDrawer(!drawerOpen))}
                                sx={{
                                    p: 1,
                                    bgcolor: themeColors.primary.main,
                                    color: themeColors.primary.contrastText,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        bgcolor: themeColors.primary.dark,
                                        transform: 'scale(1.05)'
                                    }
                                }}
                            >
                                <MenuIcon sx={{ fontSize: '1.3rem' }} />
                            </IconButton>
                        )}
                    </Box>

                    <ButtonGroup
                        variant="outlined"
                        sx={{
                            '& .MuiButton-root': {
                                bgcolor: themeColors.background.default,
                                color: themeColors.primary.main,
                                borderColor: themeColors.primary.main,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    bgcolor: themeColors.primary.lighter,
                                    color: themeColors.primary.dark,
                                    borderColor: themeColors.primary.main,
                                    transform: 'translateY(-1px)'
                                },
                                '&.active': {
                                    bgcolor: themeColors.primary.main,
                                    color: themeColors.primary.contrastText,
                                    borderColor: themeColors.primary.main,
                                    '&:hover': {
                                        bgcolor: themeColors.primary.dark
                                    }
                                }
                            }
                        }}
                    >
                        <Button
                            className={activeButton === 'request' ? 'active' : ''}
                            onClick={handleOpenPlanRequestDialog}
                            startIcon={<FlightTakeoffIcon />}
                            sx={{
                                minWidth: { xs: 'auto', sm: '120px' },
                                px: { xs: 1.5, sm: 2 }
                            }}
                        >
                            <Typography
                                variant="button"
                                sx={{
                                    display: { xs: 'none', sm: 'block' },
                                    fontSize: '0.875rem'
                                }}
                            >
                                Request & Plan
                            </Typography>
                            <Typography
                                variant="button"
                                sx={{
                                    display: { xs: 'block', sm: 'none' },
                                    fontSize: '0.875rem'
                                }}
                            >
                                Trip Request
                            </Typography>
                        </Button>
                        <Button
                            className={activeButton === 'plan' ? 'active' : ''}
                            onClick={handleDirectTravelPlan}
                            startIcon={<FlightTakeoffIcon />}
                            sx={{
                                minWidth: { xs: 'auto', sm: '120px' },
                                px: { xs: 1.5, sm: 2 }
                            }}
                        >
                            <Typography
                                variant="button"
                                sx={{
                                    display: { xs: 'none', sm: 'block' },
                                    fontSize: '0.875rem'
                                }}
                            >
                                Travel Plan
                            </Typography>
                            <Typography
                                variant="button"
                                sx={{
                                    display: { xs: 'block', sm: 'none' },
                                    fontSize: '0.875rem'
                                }}
                            >
                                Plan
                            </Typography>
                        </Button>
                    </ButtonGroup>

                    <Box sx={{ flexGrow: 1 }} />

                    <Tooltip title={formatDate(currentTime)}>
                        <TimeBox>
                            <AccessTimeIcon
                                sx={{
                                    color: themeColors.primary.main
                                }}
                                aria-hidden="true"
                            />
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        color: themeColors.primary.main
                                    }}
                                >
                                    {formatTime(currentTime)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    className="date-display"
                                    sx={{
                                        color: themeColors.primary.light
                                    }}
                                >
                                    {formatDate(currentTime)}
                                </Typography>
                            </Box>
                        </TimeBox>
                    </Tooltip>

                    <NotificationBell />
                    <ProfileSection />

                    {matchDownSm && (
                        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                            <MobileSection />
                        </Box>
                    )}
                </Box>
            )}

            <TripPlanSearchDialog
                open={openPlanRequestDialog}
                onClose={handleClosePlanRequestDialog}
                onOpenTravelPlanForm={handleOpenTravelPlanForm}
                onOpenTravelRequestForm={handleOpenTravelRequestForm}
            />
            <TravelPlanForm
                open={openTravelPlanForm}
                handleClose={handleCloseTravelPlanForm}
                onOpenTravelRequest={handleTravelPlanSuccess}
            />
            <TravelRequestForm open={openTravelRequestForm} handleClose={handleCloseTravelRequestForm} selectedPlan={selectedPlan} />
        </>
    );
};

export default Header;
